<template>
  <div
    v-if="applicationReady && componentReady"
    class="questionnaire"
  >
    <div class="questionnaire-top">
      <b-row>
        <b-col
          v-if="!formSubmitted"
          cols="12"
        >
          <b-row>
            <b-col
              cols="12"
              :class="questionClasses"
            >
              <h1 v-if="currentPage === 1">
                {{ replaceText(currentQuestion.question, ['VAR1'], ['firstname']) }}
              </h1>
              <h1 v-else-if="currentPage === 2">
                Take and record vital signs
              </h1>
              <h1 v-else-if="currentPage === 3">
                Take and record <u>optional</u> vital signs
              </h1>
              <h1 v-else-if="currentPage === 4">
                Enter date and time of Health Check
              </h1>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12">
              <b-row class="question-errors">
                <b-col
                  v-if="currentQuestion.section === 'residentExperiencedAnyOfTheseSymptoms' && $v.formAnswers.residentExperiencedAnyOfTheseSymptoms.$error"
                  cols="12"
                  class="pb-3"
                >
                  <div
                    v-if="!$v.formAnswers.residentExperiencedAnyOfTheseSymptoms.required || !$v.formAnswers.residentExperiencedAnyOfTheseSymptoms.mustBeTrue"
                    class="invalid-feedback"
                  >
                    <h3>Choose an option to continue</h3>
                  </div>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12">
              <smooth-reflow>
                <b-form @submit.prevent>
                  <b-form-row
                    v-if="currentPage === 1"
                    class="question-answer"
                  >
                    <b-col
                      cols="12"
                      class="pb-3 px-4"
                    >
                      <b-form-row class="question-checkboxes">
                        <b-col
                          v-for="value in currentQuestion.values"
                          :key="value.id"
                          cols="12"
                          md="6"
                          class="pb-3 px-4"
                        >
                          <b-form-checkbox
                            :class="questionCheckboxClasses(value)"
                            size="lg"
                            v-model="formAnswers.residentExperiencedAnyOfTheseSymptoms[value.id]"
                            :disabled="checkboxDisabledState[currentQuestion.id][value.id]"
                            text-field="answer_text"
                            value-field="answer_value"
                          >
                            {{ value.answer_text }}
                          </b-form-checkbox>
                        </b-col>
                      </b-form-row>
                    </b-col>
                  </b-form-row>

                  <b-form-row
                    v-if="currentPage === 2"
                    class="question-answer"
                  >
                    <b-col cols="12">
                      <b-form-row class="question-answer">
                        <b-col
                          cols="12"
                          md="4"
                          class="pb-3 text-md-right"
                        >
                          <h1>
                            Temperature
                          </h1>
                        </b-col>

                        <b-col
                          cols="12"
                          md="5"
                          class="pb-3"
                        >
                          <b-form-input
                            size="lg"
                            type="number"
                            v-model="formAnswers.temperature"
                            step=".1"
                            placeholder="i.e. 98.6"
                            onfocus="this.placeholder = ''"
                            onblur="this.placeholder = 'i.e. 98.6'"
                          />
                        </b-col>

                        <b-col
                          cols="12"
                          md="3"
                          class="pb-3"
                        >
                          <h3 class="question-lable">
                            &deg;F
                          </h3>
                        </b-col>
                      </b-form-row>

                      <b-row
                        v-if="$v.formAnswers.temperature.$error"
                      >
                        <b-col cols="12">
                          <b-row class="question-errors">
                            <b-col
                              cols="12"
                              class="pb-3"
                            >
                              <div
                                v-if="!$v.formAnswers.temperature.required"
                                class="invalid-feedback"
                              >
                                <h3>Temperature is required</h3>
                              </div>

                              <div
                                v-if="!$v.formAnswers.temperature.decimal"
                                class="invalid-feedback"
                              >
                                <h3>Temperature must be a decimal</h3>
                              </div>

                              <div
                                v-if="!$v.formAnswers.temperature.between"
                                class="invalid-feedback"
                              >
                                <h3>Enter a temperature between {{ lowTemp }}&deg; and {{ highTemp }}&deg; F to continue</h3>
                              </div>
                            </b-col>
                          </b-row>
                        </b-col>
                      </b-row>

                      <b-form-row class="question-answer">
                        <b-col
                          cols="12"
                          md="4"
                          class="pb-3 text-md-right"
                        >
                          <h1>
                            Pulse
                          </h1>
                        </b-col>

                        <b-col
                          cols="12"
                          md="5"
                          class="pb-3"
                        >
                          <b-form-input
                            size="lg"
                            type="number"
                            v-model="formAnswers.pulse"
                            placeholder="i.e. 76"
                            onfocus="this.placeholder = ''"
                            onblur="this.placeholder = 'i.e. 76'"
                          />
                        </b-col>

                        <b-col
                          cols="12"
                          md="3"
                          class="pb-3"
                        >
                          <h3 class="question-lable">
                            beats per min
                          </h3>
                        </b-col>
                      </b-form-row>

                      <b-row
                        v-if="$v.formAnswers.pulse.$error"
                      >
                        <b-col cols="12">
                          <b-row class="question-errors">
                            <b-col
                              cols="12"
                              class="pb-3"
                            >
                              <div
                                v-if="!$v.formAnswers.pulse.required"
                                class="invalid-feedback"
                              >
                                <h3>Pulse is required</h3>
                              </div>

                              <div
                                v-if="!$v.formAnswers.pulse.numeric"
                                class="invalid-feedback"
                              >
                                <h3>Pulse rate must be a numeric</h3>
                              </div>

                              <div
                                v-if="!$v.formAnswers.pulse.between"
                                class="invalid-feedback"
                              >
                                <h3>Enter a pulse rate between {{ lowPulse }} and {{ highPulse }} to continue</h3>
                              </div>
                            </b-col>
                          </b-row>
                        </b-col>
                      </b-row>

                      <b-form-row class="question-answer">
                        <b-col
                          cols="12"
                          md="4"
                          class="pb-3 text-md-right"
                        >
                          <h1>
                            Respiration
                          </h1>
                        </b-col>

                        <b-col
                          cols="12"
                          md="5"
                          class="pb-3"
                        >
                          <b-form-input
                            size="lg"
                            type="number"
                            v-model="formAnswers.respiration"
                            placeholder="i.e. 15"
                            onfocus="this.placeholder = ''"
                            onblur="this.placeholder = 'i.e. 15'"
                            @keyup.enter="checkQuestion"
                          />
                        </b-col>

                        <b-col
                          cols="12"
                          md="3"
                          class="pb-3"
                        >
                          <h3 class="question-lable">
                            breaths per min
                          </h3>
                        </b-col>
                      </b-form-row>

                      <b-row
                        v-if="$v.formAnswers.respiration.$error"
                      >
                        <b-col cols="12">
                          <b-row class="question-errors">
                            <b-col
                              cols="12"
                              class="pb-3"
                            >
                              <div
                                v-if="!$v.formAnswers.respiration.required"
                                class="invalid-feedback"
                              >
                                <h3>Respiration is required</h3>
                              </div>

                              <div
                                v-if="!$v.formAnswers.respiration.numeric"
                                class="invalid-feedback"
                              >
                                <h3>Respiration must be a numeric</h3>
                              </div>

                              <div
                                v-if="!$v.formAnswers.respiration.between"
                                class="invalid-feedback"
                              >
                                <h3>Enter a respiration between {{ lowRespiration }} and {{ highRespiration }} to continue</h3>
                              </div>
                            </b-col>
                          </b-row>
                        </b-col>
                      </b-row>
                    </b-col>
                  </b-form-row>

                  <b-form-row
                    v-if="currentPage === 3"
                    class="question-answer"
                  >
                    <b-col cols="12">
                      <b-form-row class="question-answer">
                        <b-col
                          cols="12"
                          md="4"
                          class="pb-3 text-md-right"
                        >
                          <h1>
                            Oxygen
                          </h1>
                        </b-col>

                        <b-col
                          cols="12"
                          md="6"
                          class="pb-3"
                        >
                          <b-form-input
                            size="lg"
                            type="number"
                            v-model="formAnswers.oxygen"
                            step=".1"
                            placeholder="i.e. 98%"
                            onfocus="this.placeholder = ''"
                            onblur="this.placeholder = 'i.e. 98%'"
                          />
                        </b-col>

                        <b-col
                          cols="12"
                          md="2"
                          class="pb-3"
                        >
                          <h3 class="question-lable">
                            %
                          </h3>
                        </b-col>
                      </b-form-row>

                      <b-row
                        v-if="$v.formAnswers.oxygen.$error"
                      >
                        <b-col cols="12">
                          <b-row class="question-errors">
                            <b-col
                              cols="12"
                              class="pb-3"
                            >
                              <div
                                v-if="!$v.formAnswers.oxygen.numeric"
                                class="invalid-feedback"
                              >
                                <h3>Oxygen must be a numeric</h3>
                              </div>

                              <div
                                v-if="!$v.formAnswers.oxygen.between"
                                class="invalid-feedback"
                              >
                                <h3>Enter a oxygen level between {{ lowOxygen }} and {{ highOxygen }} to continue</h3>
                              </div>
                            </b-col>
                          </b-row>
                        </b-col>
                      </b-row>

                      <b-form-row class="question-answer">
                        <b-col
                          cols="12"
                          md="4"
                          class="pb-3 display-flex flex-column justify-content-end"
                        >
                          <div class="d-flex flex-row justify-content-md-end">
                            <h1>
                              Blood Pressure
                            </h1>
                          </div>
                        </b-col>

                        <b-col
                          cols="12"
                          md="2"
                          class="pb-3"
                        >
                          <label>Systolic</label>
                          <b-form-input
                            size="lg"
                            type="number"
                            v-model="formAnswers.systolic"
                            placeholder="i.e. 120"
                            onfocus="this.placeholder = ''"
                            onblur="this.placeholder = 'i.e. 120'"
                          />
                        </b-col>

                        <b-col
                          cols="12"
                          md="2"
                          class="pb-3 display-flex flex-column justify-content-end"
                        >
                          <h3 class="question-lable">
                            mmHG
                          </h3>
                        </b-col>

                        <b-col
                          cols="12"
                          md="2"
                          class="pb-3"
                        >
                          <label>Diastolic</label>
                          <b-form-input
                            size="lg"
                            type="number"
                            v-model="formAnswers.diastolic"
                            placeholder="i.e. 80"
                            onfocus="this.placeholder = ''"
                            onblur="this.placeholder = 'i.e. 80'"
                            @keyup.enter="checkQuestion"
                          />
                        </b-col>

                        <b-col
                          cols="12"
                          md="2"
                          class="pb-3 display-flex flex-column justify-content-end"
                        >
                          <h3 class="question-lable">
                            mmHG
                          </h3>
                        </b-col>
                      </b-form-row>

                      <b-row
                        v-if="$v.formAnswers.systolic.$error || $v.formAnswers.diastolic.$error"
                      >
                        <b-col cols="12">
                          <b-row class="question-errors">
                            <b-col
                              cols="12"
                              class="pb-3"
                            >
                              <div
                                v-if="!$v.formAnswers.systolic.numeric"
                                class="invalid-feedback"
                              >
                                <h3>Systolic must be a numeric</h3>
                              </div>

                              <div
                                v-if="!$v.formAnswers.systolic.between"
                                class="invalid-feedback"
                              >
                                <h3>Enter a systolic between {{ lowSystolic }}/{{ highSystolic }} to continue</h3>
                              </div>

                              <div
                                v-if="!$v.formAnswers.diastolic.numeric"
                                class="invalid-feedback"
                              >
                                <h3>Diastolic must be a numeric</h3>
                              </div>

                              <div
                                v-if="!$v.formAnswers.diastolic.between"
                                class="invalid-feedback"
                              >
                                <h3>Enter a diastolic between {{ lowDiastolic }}/{{ highDiastolic }} to continue</h3>
                              </div>
                            </b-col>
                          </b-row>
                        </b-col>
                      </b-row>
                    </b-col>
                  </b-form-row>

                  <b-form-row
                    v-if="currentPage === 4"
                    class="question-answer"
                  >
                    <b-col cols="12">
                      <b-form-row class="question-answer">
                        <b-col
                          cols="12"
                          md="3"
                          class="pb-3"
                          offset-md="3"
                        >
                          <b-form-datepicker
                            id="example-datepicker"
                            v-model="formAnswers.enterDateOfHealthCheck"
                            class="mb-2"
                            :max="max"
                            :date-format-options="dateFormatOptions"
                          />
                        </b-col>

                        <b-col
                          cols="12"
                          md="3"
                          class="pb-3"
                        >
                          <b-form-timepicker
                            v-model="formAnswers.enterTimeOfHealthCheck"
                            locale="en"
                          />
                        </b-col>
                      </b-form-row>
                    </b-col>
                  </b-form-row>
                </b-form>
              </smooth-reflow>
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <b-row>
        <b-col
          v-if="formSubmitted"
          cols="12"
          class="screening-complete"
        >
          <b-row>
            <b-col
              cols="12"
              class="screening-complete-title"
            >
              <span class="success-message exit">
                <h1>
                  Health Check Complete!
                </h1>
              </span>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>

    <div class="questionnaire-bottom">
      <b-row>
        <b-col cols="12">
          <b-form>
            <b-form-row class="justify-content-center no-gutters">
              <b-col
                v-if="!formSubmitted"
                cols="6"
                sm="4"
                class="pb-3 question-navigation order-md-2"
              >
                <b-button
                  @click="previousPage"
                  :disabled="canGoBack"
                  class="btn-green btn-nav"
                >
                  <b-icon-caret-left-fill />
                </b-button>
              </b-col>

              <b-col
                v-if="!canSubmitForm"
                cols="6"
                sm="4"
                class="pb-3 question-navigation order-md-3"
              >
                <b-button
                  @click="checkQuestion"
                  class="btn-green btn-nav"
                >
                  <b-icon-caret-right-fill />
                </b-button>
              </b-col>

              <b-col
                v-if="canSubmitForm && !formSubmitted"
                cols="6"
                sm="4"
                class="pb-3 question-navigation order-md-3"
              >
                <b-button
                  @click="checkQuestion"
                  class="btn-green btn-nav"
                >
                  Submit
                </b-button>
              </b-col>

              <b-col
                v-if="formSubmitted"
                cols="6"
                sm="4"
                class="pb-3 question-navigation"
              >
                <b-button
                  @click="goHome"
                  class="btn-green btn-nav"
                >
                  <img
                    src="../assets/home-icon.png"
                    class="screening-icon"
                  >
                </b-button>
              </b-col>

              <b-col
                v-if="!formSubmitted"
                cols="6"
                sm="4"
                class="pb-3 question-navigation order-md-1"
              >
                <b-button
                  @click="goHome"
                  class="btn-green btn-nav order-md-1"
                >
                  <img
                    src="../assets/home-icon.png"
                  >
                </b-button>
              </b-col>

              <b-col
                v-if="formSubmitted"
                cols="6"
                sm="4"
                class="pb-3 question-navigation"
              >
                <b-button
                  @click="logout"
                  class="btn-green btn-nav"
                >
                  <img
                    src="../assets/log-out-icon-white.png"
                    class="icon-with-text"
                  >
                  Log out
                </b-button>
              </b-col>
            </b-form-row>
          </b-form>
        </b-col>
      </b-row>
    </div>

    <b-modal
      v-model="showFailedScreeningModal"
      id="failed-screening"
      modal-class="failed-screening"
      centered
      no-close-on-esc
      hide-header
      hide-footer
    >
      <div class="failed-screening-icon">
        <img
          src="../assets/warning-icon.png"
          class="icon"
        >
      </div>

      <p class="my-4 text-center">
        Confirm {{ selectedUserName }}
        <ul class="confirm-messsages">
          <li
            v-for="(message, index) in confirmMessages"
            :key="index"
          >
            {{ message }}
          </li>
        </ul>
      </p>

      <b-row>
        <b-col
          cols="12"
          class="pb-3 text-left"
        >
          <b-button
            variant="green"
            class="failed-screening-option"
            @click.stop.prevent="confirmed"
          >
            Confirm &raquo;
          </b-button>
        </b-col>
      </b-row>

      <b-row>
        <b-col
          cols="12"
        >
          <b-button
            variant="green"
            class="failed-screening-option"
            @click="showFailedScreeningModal = !showFailedScreeningModal"
          >
            &laquo; Go back
          </b-button>
        </b-col>
      </b-row>

      <template v-slot:modal-header-close>
        &nbsp;
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  LOW_TEMP,
  HIGH_TEMP,
  CONFIRM_LOW_TEMP,
  CONFIRM_HIGH_TEMP,
  LOW_PULSE,
  HIGH_PULSE,
  CONFIRM_LOW_PULSE,
  CONFIRM_HIGH_PULSE,
  LOW_RESPIRATION,
  HIGH_RESPIRATION,
  CONFIRM_LOW_RESPIRATION,
  CONFIRM_HIGH_RESPIRATION,
  LOW_OXYGEN,
  HIGH_OXYGEN,
  CONFIRM_LOW_OXYGEN,
  LOW_SYSTOLIC,
  HIGH_SYSTOLIC,
  CONFIRM_LOW_SYSTOLIC,
  CONFIRM_HIGH_SYSTOLIC,
  LOW_DIASTOLIC,
  HIGH_DIASTOLIC,
  CONFIRM_LOW_DIASTOLIC,
  CONFIRM_HIGH_DIASTOLIC
} from '../store/modules/form-types'
import { between, decimal, numeric, required } from 'vuelidate/lib/validators'
import { mustBeTrue } from '../plugins/validators'
import mixin from '../mixins/forms.js'

export default {
  middleware: 'auth',
  name: 'HealthCheck',
  mixins: [mixin],
  components: {
    'smooth-reflow': () => import(/* webpackPrefetch: true */ /* webpackChunkName: "smooth-reflow" */ '../components/SmoothReflow')
  },
  data () {
    return {
      confirmMessages: [],
      formAnswers: {
        residentExperiencedAnyOfTheseSymptoms: [],
        temperature: '',
        pulse: '',
        respiration: '',
        oxygen: '',
        systolic: '',
        diastolic: '',
        enterDateOfHealthCheck: '',
        enterTimeOfHealthCheck: ''
      }
    }
  },
  validations: {
    formAnswers: {
      residentExperiencedAnyOfTheseSymptoms: {
        required,
        mustBeTrue
      },
      temperature: {
        required,
        decimal,
        between: between(LOW_TEMP, HIGH_TEMP)
      },
      pulse: {
        required,
        numeric,
        between: between(LOW_PULSE, HIGH_PULSE)
      },
      respiration: {
        required,
        numeric,
        between: between(LOW_RESPIRATION, HIGH_RESPIRATION)
      },
      oxygen: {
        numeric,
        between: between(LOW_OXYGEN, HIGH_OXYGEN)
      },
      systolic: {
        numeric,
        between: between(LOW_SYSTOLIC, HIGH_SYSTOLIC)
      },
      diastolic: {
        numeric,
        between: between(LOW_DIASTOLIC, HIGH_DIASTOLIC)
      },
      enterDateOfHealthCheck: {
        required
      },
      enterTimeOfHealthCheck: {
        required
      }
    }
  },
  mounted () {
    this.init()
  },
  computed: {
    componentReady () {
      if (this.loggedIn) {
        return this.currentQuestionnaire !== undefined &&
          this.questions !== undefined &&
          this.questionAnswersAndStatesSetup
      }
      return false
    },
    checkboxDisabledLength () {
      return this.checkboxDisabledState.length
    },
    residentExperiencedAnyOfTheseSymptoms () {
      return this.formAnswers.residentExperiencedAnyOfTheseSymptoms
    },
    questionDate () {
      return this.findQuestionBySection('enterDateOfHealthCheck')
    },
    questionTime () {
      return this.findQuestionBySection('enterTimeOfHealthCheck')
    },
    questionTemperature () {
      return this.findQuestionBySection('temperature')
    },
    questionPulse () {
      return this.findQuestionBySection('pulse')
    },
    questionRespiration () {
      return this.findQuestionBySection('respiration')
    },
    questionOxygen () {
      return this.findQuestionBySection('oxygen')
    },
    questionSystolic () {
      return this.findQuestionBySection('systolic')
    },
    questionDiastolic () {
      return this.findQuestionBySection('diastolic')
    }
  },
  watch: {
    residentExperiencedAnyOfTheseSymptoms: {
      handler: 'setDisabledStateForMoreThanTwoCheckboxes',
      deep: true
    }
  },
  methods: {
    async init () {
      await this.setCurrentDateAndTime('healthcheck')
      await this.setCurrentPage(1)
      await this.isMultiStep()
    },
    checkQuestion () {
      if (this.currentPage === 2) {
        this.$v.formAnswers.temperature.$touch()
        this.$v.formAnswers.pulse.$touch()
        this.$v.formAnswers.respiration.$touch()
        if (
          !this.$v.formAnswers.temperature.$invalid &&
          !this.$v.formAnswers.pulse.$invalid &&
          !this.$v.formAnswers.respiration.$invalid
        ) {
          this.confirmMessages = []
          let answers = this.answersById[this.questionTemperature.id]
          if (parseFloat(answers) <= parseFloat(CONFIRM_LOW_TEMP)) {
            this.confirmMessages.push('Has a temperature below ' + CONFIRM_LOW_TEMP + '°F')
          }
          if (parseFloat(answers) >= parseFloat(CONFIRM_HIGH_TEMP)) {
            this.confirmMessages.push('Has a temperature over ' + CONFIRM_HIGH_TEMP + '°F')
          }

          answers = this.answersById[this.questionPulse.id]
          if (parseInt(answers) <= parseInt(CONFIRM_LOW_PULSE)) {
            this.confirmMessages.push('Has a pulse rate below ' + CONFIRM_LOW_PULSE + ' BPM')
          }
          if (parseInt(answers) >= parseInt(CONFIRM_HIGH_PULSE)) {
            this.confirmMessages.push('Has a pulse rate over ' + CONFIRM_HIGH_PULSE + ' BPM')
          }

          answers = this.answersById[this.questionRespiration.id]
          if (parseInt(answers) <= parseInt(CONFIRM_LOW_RESPIRATION)) {
            this.confirmMessages.push('Has a respiration rate below ' + CONFIRM_LOW_RESPIRATION + ' RPM')
          }
          if (parseInt(answers) >= parseInt(CONFIRM_HIGH_RESPIRATION)) {
            this.confirmMessages.push('Has a respiration rate over ' + CONFIRM_HIGH_RESPIRATION + ' RPM')
          }

          if (this.confirmMessages.length) {
            // this.setFailedScreening(true)
            // this.setFailureDescription(this.selectedUser.itemName + ' has failed the health check with one or more symptoms.')
            this.showFailedScreeningModal = !this.showFailedScreeningModal
          } else {
            this.nextPage()
          }
        }
      } else if (this.currentPage === 3) {
        this.$v.formAnswers.oxygen.$touch()
        this.$v.formAnswers.systolic.$touch()
        this.$v.formAnswers.diastolic.$touch()
        if (
          !this.$v.formAnswers.oxygen.$invalid &&
          !this.$v.formAnswers.systolic.$invalid &&
          !this.$v.formAnswers.diastolic.$invalid
        ) {
          this.confirmMessages = []
          let answers = this.answersById[this.questionOxygen.id]
          if (parseFloat(answers) <= parseFloat(CONFIRM_LOW_OXYGEN)) {
            this.confirmMessages.push('Has an oxygen level below ' + CONFIRM_LOW_OXYGEN + ' Sp02%')
          }

          answers = this.answersById[this.questionSystolic.id]
          if (parseInt(answers) < parseInt(CONFIRM_LOW_SYSTOLIC)) {
            this.confirmMessages.push('Has a systolic reading below ' + CONFIRM_LOW_SYSTOLIC + ' mmHg')
          }
          if (parseInt(answers) >= parseInt(CONFIRM_HIGH_SYSTOLIC)) {
            this.confirmMessages.push('Has a systolic reading over ' + CONFIRM_HIGH_SYSTOLIC + ' mmHg')
          }

          answers = this.answersById[this.questionDiastolic.id]
          if (parseInt(answers) <= parseInt(CONFIRM_LOW_DIASTOLIC)) {
            this.confirmMessages.push('Has a diastolic reading below ' + CONFIRM_LOW_DIASTOLIC + ' mmHg')
          }
          if (parseInt(answers) >= parseInt(CONFIRM_HIGH_DIASTOLIC)) {
            this.confirmMessages.push('Has a diastolic reading over ' + CONFIRM_HIGH_DIASTOLIC + ' mmHg')
          }

          if (this.confirmMessages.length) {
            // this.setFailedScreening(true)
            // this.setFailureDescription(this.selectedUser.itemName + ' has failed the health check with one or more symptoms.')
            this.showFailedScreeningModal = !this.showFailedScreeningModal
          } else {
            this.nextPage()
          }
        }
      } else if (this.currentPage === 4) {
        this.$v.formAnswers.enterDateOfHealthCheck.$touch()
        this.$v.formAnswers.enterTimeOfHealthCheck.$touch()
        if (
          !this.$v.formAnswers.enterDateOfHealthCheck.$invalid &&
          !this.$v.formAnswers.enterTimeOfHealthCheck.$invalid
        ) {
          this.submitForm()
        }
      } else {
        this.$v.formAnswers[this.currentQuestion.section].$touch()
        if (!this.$v.formAnswers[this.currentQuestion.section].$invalid) {
          const continueToNextPage = true

          if (this.canSubmitForm && continueToNextPage) {
            this.submitForm()
          }

          if (continueToNextPage) {
            this.nextPage()
          }
        }
      }
    },
    submitForm () {
      this.setFormSubmitted(!this.formSubmitted)
      this.addFormResults(this.answersById)
    },
    confirmed () {
      this.showFailedScreeningModal = !this.showFailedScreeningModal
      this.confirmMessages = []
      this.nextPage()
    }
  }
}
</script>

<style scoped lang="scss">
.questionnaire-bottom {
  text-align: center;
}
.question-answer {
  label {
    color: #636466;
    display: block;
    font-size: 1.8rem;
    text-align: center;
  }
}
.display-flex {
  display: flex;
}
h1,
h3 {
  &.align-flex-end {
    align-self: flex-end;
    display: inline-block;
  }
}
.question-lable {
  color: #d6d7d8;
  font-style: italic;
}
.confirm-messsages {
  list-style: none;
  padding-left: 0;
}
</style>
